/**
 * Registry for the programmatic collection pages at /collections/<key>.
 *
 * WHY THIS EXISTS. Every public collection gets exactly one page, and this file is the list. It
 * used to hold eighteen of the twenty-two corpora, because six hand-written pages under /apps
 * described the other four — and described two of them TWICE: /apps/case-law and
 * /apps/legislation both rendered the `law` corpus, and /apps/clinical-trials rendered `medical`,
 * which already had /collections/medicine-and-clinical-research. Identical figures on two URLs,
 * competing for one intent. Those six routes are now permanent redirects into /collections and
 * their copy was ported here verbatim, so the shape is 1:1 again.
 *
 * TWO KINDS OF ENTRY, AND THEY READ DIFFERENT FEEDS.
 *
 *   source omitted (22 entries) — backed by the corpora INSIGHTS payload. The key must resolve
 *   there or the page renders no figures at all. `CorpusPage` is deliberately silent on a miss,
 *   so it never shows a number it cannot back — which is why three of the old hand-written pages
 *   carried NO live data for an unknown period after the backend renamed `case_law` -> `law` and
 *   `clinical_trials` -> `medical`, and nothing failed loudly.
 *
 *   source: "storage" (7 entries) — live and ingesting, but belonging to no corpus, so
 *   `getCorpusInsight()` returns null for them and they appeared nowhere on the site. The STORAGE
 *   feed carries them with a real count and a real per-record size, and that is ALL it carries:
 *   no growth series, no facet breakdowns, no coverage span. They render through
 *   `SimpleCollectionPage` rather than `CorpusPage` for exactly that reason. Do not give them
 *   `variables`, `questions` or year spans — there is nothing to read those from, so any value
 *   would be invented.
 *
 * `scripts/check-corpora.mjs` asserts BOTH: insights keys against the insights payload, storage
 * keys against the storage feed. Run it after any backend rename.
 *
 * FORMATTING TRAP — READ BEFORE EDITING. `key`, `slug` and `eyebrow` are written on ONE line per
 * entry. A `//` comment inserted after `key:` comments out the rest of that line and silently
 * removes the slug, which has already happened once. Use a block comment, or put the note on its
 * own line above the entry.
 */

export type CorpusEntry = {
  /**
   * The identifier this entry is matched on in the live feed named by `source`.
   *
   * With `source` omitted that is the key in the corpora INSIGHTS payload, and it MUST resolve —
   * see the note above. With `source: "storage"` it is the collection's row in the STORAGE feed.
   *
   * In the storage case that row's `collection` and `instance` are INTERNAL STORE NAMES. They are
   * used to find the row and are never rendered: the reader-facing name is this entry's `title`.
   */
  key: string;
  /**
   * Which live feed backs this entry. Omitted means the corpora insights payload, which carries
   * growth, facets and a coverage span. `"storage"` means the collection exists in the storage
   * feed only — a count and a per-record size, and nothing else that can honestly be drawn.
   */
  source?: "storage";
  /** URL segment. Kept separate from `key` so a backend rename does not change a public URL. */
  slug: string;
  eyebrow: string;
  title: string;
  /** SERP title. The on-page `title` is written to READ well and often runs past the ~48 chars a
   *  result page shows once " — askFinz" is appended. Without this, 13 of these pages fell back to
   *  a generic "Reference corpus: <unit>" — near-duplicate titles competing with each other and
   *  containing none of the words anyone searches. Set it whenever `title` exceeds 48. */
  seoTitle?: string;
  /** Full Google result title, metadata only. See lib/seo-title.ts. */
  serpTitle?: string;
  lede: string;
  /** What one row IS — "papers", "recipes". Rendered next to the live count. */
  unit: string;
  /**
   * What a reader can do with this collection.
   *
   * `detail` is carried by the FIRST capability only, and that is on purpose rather than half a
   * job: the lead capability renders as the large tile in the bento layout and as the first full
   * row in the sparse layout, and in both it is the one with room for sub-points. Putting chips on
   * all four made every card the same weight again, which is the exact problem this section had.
   */
  capabilities: { title: string; body: string; detail?: string[] }[];
  coverage: string;
  /** `href` overrides the default `/apps/<slug>` link, for apps whose /apps page is now a redirect. */
  relatedApp: { slug: string; name: string; note: string; href?: string };
  /**
   * Fields this collection records against every item — emitted as `variableMeasured` in the
   * Dataset structured data on /collections/<slug>.
   *
   * EVERY ENTRY MUST BE SUPPORTED BY THE COPY ALREADY ON THAT PAGE. These are read by Google
   * Dataset Search as factual claims about what the collection holds, so each one below is taken
   * from that corpus's own `capabilities` or `coverage` text rather than from what we would like
   * to hold. If you cannot point at the sentence on the page that says a field is kept, leave it
   * out — an omitted variable costs a little discovery, an invented one is a false claim in
   * machine-readable form, which is worse.
   *
   * Optional on purpose: a new corpus with no verified field list simply emits no
   * `variableMeasured` rather than a guessed one.
   */
  variables?: string[];
  /**
   * What a single record IS, in one sentence.
   *
   * For the storage-backed entries only, and it exists because `variables` cannot be written for
   * them honestly: those collections publish no field list anywhere the site can read, so a list
   * of four field names would be four invented facts in the slot most likely to be believed. A
   * sentence saying what the record is can be true without claiming what is inside it.
   */
  record?: string;
  /** Heading for the `by_kind` breakdown. The payload has no per-corpus equivalent of
      `geo_title`, and "By type" is wrong wherever the underlying field is not a type.
      `null` suppresses the breakdown entirely — used where the corpus merges collections whose
      kind fields are different things, so no single heading would be truthful. */
  kindTitle?: string | null;

  /* ── DEPTH FIELDS ──────────────────────────────────────────────────────────────────────────
     Added because "what you can do with it" was four one-line assertions and read as the thinnest
     block on the page. All four are optional; a corpus without them renders exactly as before.

     THEY REACH THE PAGE BY KEY, NOT BY PROP. app/collections/[key]/page.tsx passes a fixed set of
     props to CorpusPageSSR and is owned elsewhere, so CorpusPage looks these up itself via
     `getCorpusDepth(corpusKey)`. Anything passed as a prop wins — which is how the six
     hand-written /apps pages carry their own, including the two that share the `law` key. ── */

  /**
   * Questions a reader could genuinely ask of THIS collection.
   *
   * These are safe to publish where an example RECORD would not be: a question describes what the
   * shelf is filed to answer, it asserts nothing about what is stored. There is no per-collection
   * sample endpoint, so a rendered example record or example answer would be fabricated — do not
   * add one, and do not put a count next to a question.
   */
  questions?: string[];
  /** One sentence saying what makes those questions answerable here specifically. */
  askIntro?: string;
  /** The SHAPE of what comes back — a passage with its source, a list you can narrow, a
      comparison. Never a value, never a named record. */
  returns?: { title: string; body: string }[];
  /**
   * Where the collection stops.
   *
   * Every page in this genre that is worth reading has this section, and it is the reason the rest
   * of the page can be believed. It will look deletable to someone tidying up marketing copy. It
   * is not: remove it and the page reads as a brochure.
   */
  limits?: string[];
  /** The related workspace's actual workflow, three steps. */
  workflow?: { title: string; body: string }[];
};

export const CORPORA: CorpusEntry[] = [
  {
    key: "research",
    /* Suppressed: this corpus merges collections whose kind fields differ, so by_kind mixes one document type with five licences. No single heading would be truthful. */
    kindTitle: null,
    slug: "research-and-data", eyebrow: "Reference corpus",
    serpTitle: "Research paper search: papers, preprints and data — askFinz",
    title: "Papers, preprints and the data behind them",
    lede: "Open-access research read in full — with the datasets a finding rests on kept alongside the write-up.",
    unit: "works",
    variables: ["Publication reference", "Sample size", "Study method", "Whether the finding was confirmed or contradicted"],
    coverage: "Read from open-access repositories and the publishers that run them, so a paper arrives with its identifier attached rather than as a page that happens to describe it.",
    capabilities: [
      {
        title: "Trace a claim to its study",
        body: "Find the work a figure came from, not an article summarising it.",
        detail: ["The paper, not the write-up", "Its formal reference attached", "Datasets kept alongside"],
      },
      { title: "See the design, not just the result", body: "Sample size and method travel with the paper, so a small trial cannot pass as a settled finding." },
      { title: "Separate confirmation from contradiction", body: "Studies that found an effect are kept distinct from those that did not." },
      { title: "Follow the citation", body: "Each work carries its formal reference, so a point can be cited and checked." },
    ],
    askIntro:
      "The design travels with the paper — sample size, method, whether the effect held up — so an answer can be qualified rather than simply asserted.",
    questions: [
      "Which studies tested this in adults over 65, and how many people did they enrol?",
      "What is the sample size behind the figure everyone keeps quoting?",
      "Which papers failed to reproduce a result that was reported widely?",
      "What datasets were published alongside the findings in this field?",
    ],
    returns: [
      { title: "A passage with its paper attached", body: "The section that supports the point, carrying the publication reference it came from." },
      { title: "A list you can narrow", body: "Filter what came back by field, by year, or by whether the work is open access." },
      { title: "Studies set against each other", body: "Work that found an effect and work that did not, side by side rather than blended into one summary." },
    ],
    limits: [
      "Subscription journals. What is held is open-access work and the metadata publishers release — a paywalled paper is not here in full text.",
      "It is not a systematic review. It will show you the studies; deciding what the weight of evidence says is still your judgement.",
    ],
    workflow: [
      { title: "Describe the finding, not the title", body: "You rarely know the paper's name. Describe the result you are chasing and the study is what comes back." },
      { title: "Qualify it before you use it", body: "Method and sample size are on the record, so one small trial does not get quoted as a settled fact." },
      { title: "Cite it from the notebook", body: "Research keeps the reference with the passage, so the note you write already carries something checkable." },
    ],
    relatedApp: { slug: "research", name: "Research", note: "The Research workspace reads this collection directly, so a notebook can cite the study rather than a page about it." },
  },
  {
    key: "news", slug: "news-and-briefings", eyebrow: "Reference corpus",
    serpTitle: "News archive search across thousands of publishers — askFinz",
    title: "Reporting from thousands of publishers, de-duplicated",
    seoTitle: "News archive: thousands of publishers",
    lede: "The day's coverage read as it is published, with outlets running the same wire copy recognised as one story.",
    unit: "articles",
    variables: ["Publishing outlet", "Publication time", "Shared-wire story cluster"],
    coverage: "Read from publishers directly as they publish, so an article carries its outlet and timestamp rather than arriving as an untraceable copy.",
    capabilities: [
      {
        title: "Follow a story across outlets",
        body: "See who covered what, side by side, instead of one account at a time.",
        detail: ["Outlet on every line", "Wire copies grouped", "Ordered by the clock"],
      },
      { title: "Twenty results, not twenty sources", body: "Outlets carrying the same wire copy are grouped, so breadth is real rather than apparent." },
      { title: "Separate fact from framing", body: "Disagreement about what happened is distinguished from disagreement about how to read it." },
      { title: "Keep the clock attached", body: "Every item carries its publication time, so a developing story reads in order." },
    ],
    askIntro:
      "Because the outlet and the publication time stay attached to every article, a story can be read in the order it broke rather than as one flattened summary.",
    questions: [
      "Who else reported this, and did any of them add reporting of their own?",
      "How did the account of this change over the first forty-eight hours?",
      "Which outlets are running the same wire copy under different headlines?",
      "What was actually said, as opposed to how it was characterised afterwards?",
    ],
    returns: [
      { title: "One story, several accounts", body: "Coverage grouped by the event it describes, so twenty results are twenty accounts and not one copied twenty times." },
      { title: "A sequence you can read forward", body: "Items in publication order, which is what makes a correction visible as a correction." },
      { title: "The outlet on every line", body: "Who published it is never separated from what was published." },
    ],
    limits: [
      "It does not adjudicate. Where outlets disagree about what happened, both accounts are kept and nothing here marks one of them true.",
      "It is not a paper of record. Coverage is strongest from the point a publisher was first read, not backwards through its whole archive.",
    ],
    workflow: [
      { title: "Ask about the event, not the headline", body: "Headlines are the least stable part of a story. Describe what happened and the coverage clusters around it." },
      { title: "Read the accounts side by side", body: "Grouping the shared wire copy is what leaves you with the outlets that actually reported something." },
      { title: "Get it as a briefing", body: "News builds the day's read from this collection, which is why the same story does not arrive five times." },
    ],
    relatedApp: { slug: "news", name: "News", note: "The News workspace builds its briefing from this collection, which is why the same story does not arrive five times." },
  },
  {
    key: "encyclopedia", slug: "encyclopedia", eyebrow: "Reference corpus",
    serpTitle: "Encyclopedia and reference search — askFinz",
    title: "Reference knowledge, read from the wikis that maintain it",
    seoTitle: "Encyclopedia and reference index",
    lede: "The settled account of a subject, read from the maintained entry rather than a copy of it.",
    unit: "entries",
    variables: ["Current revision", "Recorded disagreement"],
    coverage: "Read from the reference works themselves, so corrections made upstream have already been applied by the time you read it.",
    capabilities: [
      {
        title: "Get the settled position first",
        body: "The accepted account leads, rather than the loudest argument about it.",
        detail: ["The maintained entry", "Corrections already applied", "Disputes kept visible"],
      },
      { title: "See where it is genuinely disputed", body: "Where the reference records an active disagreement, that is surfaced rather than flattened." },
      { title: "Read the current revision", body: "The maintained entry, not a snapshot taken at some unknown point." },
      { title: "Start from common ground", body: "Useful as the shared baseline before a question gets specialised." },
    ],
    askIntro:
      "This is the shelf for the question before the question — the definition, the background, the shape of a subject you are about to go deeper into.",
    questions: [
      "What is the accepted definition of this term, and who disagrees with it?",
      "What does the entry say now, after the corrections made to it?",
      "Where does a reference work record an open dispute rather than a settled answer?",
      "What background do I need before a specialist answer will make any sense?",
    ],
    returns: [
      { title: "The settled account first", body: "The maintained entry, with the argument about it kept separate from it." },
      { title: "The disagreement, where there is one", body: "A recorded dispute surfaced rather than averaged into a bland middle." },
      { title: "A footing for the specialised shelves", body: "Enough common ground that a question can then be asked of research, law or standards." },
    ],
    limits: [
      "It is not authority. A reference entry is a good place to start and a poor thing to cite — for that, use the research, law or standards shelves.",
      "It is thin on the very new. An entry has to be written and maintained before there is anything here to read.",
    ],
    workflow: [
      { title: "Ask the plain question", body: "What is it, who is it, how does it work — the questions people are slightly embarrassed to ask." },
      { title: "Take the definition, not the argument", body: "The accepted account leads, so you are not reading a talk page to find out what a word means." },
      { title: "Move on to the specialist shelf", body: "Search reaches here first for definitional questions, which is why a plain answer arrives without a detour." },
    ],
    relatedApp: { slug: "search", name: "Search", note: "Search reaches this collection first for definitional questions, which is why a plain 'what is' answer arrives without a detour." },
  },
  {
    key: "sector", slug: "industry-and-sector", eyebrow: "Reference corpus",
    serpTitle: "Industry and sector research search — askFinz",
    title: "How industries describe themselves, in their own words",
    seoTitle: "Industry and sector research",
    lede: "Trade bodies and companies explaining their own market — where segment names and category definitions actually originate.",
    unit: "documents",
    variables: ["Publishing body", "Segment definition", "Whether the publisher is a trade body or a vendor"],
    coverage: "Read from trade associations, standards bodies and companies publishing about their own sector, rather than from commentary written at a distance.",
    capabilities: [
      {
        title: "Use the sector's own vocabulary",
        body: "Segment definitions come from the people who coined them.",
        detail: ["Primary documents", "Publisher always named", "Trade body vs vendor"],
      },
      { title: "Tell a trade body from a vendor", body: "An industry position and one company's marketing are kept apart." },
      { title: "Find the structure of a market", body: "How an industry divides itself is usually the first thing an outsider needs." },
      { title: "Read the primary material", body: "The published document, not a summary of a summary." },
    ],
    askIntro:
      "The value of this shelf is that it is partial. It holds what an industry says about itself, which is where the words everyone else borrows are actually defined.",
    questions: [
      "How does this industry divide itself into segments, and who decided those names?",
      "What does the trade body say about this market, as against a vendor selling into it?",
      "Which organisations publish their own figures for this sector?",
      "What vocabulary do insiders use for the thing I keep describing in plain words?",
    ],
    returns: [
      { title: "The primary document", body: "What the body actually published, rather than commentary written at a distance from it." },
      { title: "The publisher, always named", body: "Who wrote it is half the information, so it is never separated from what was written." },
      { title: "A map of a market", body: "Segment definitions in the terms the sector itself uses, which is what makes further searching work." },
    ],
    limits: [
      "It is not neutral analysis. This is what an industry says about itself — read it as a source, never as a verdict.",
      "It is not a market-size database. Where a figure appears it belongs to whoever published it, with all that implies.",
    ],
    workflow: [
      { title: "Ask in plain words", body: "You do not yet know the segment name. Describe the market and the documents that define it come back." },
      { title: "Check who published it", body: "A trade association and a vendor are both worth reading, for different reasons, and they are kept apart." },
      { title: "Carry the vocabulary onward", body: "Research reads this collection when the question is about a market rather than a company." },
    ],
    relatedApp: { slug: "research", name: "Research", note: "Research reads this collection when a question is about a market rather than a company." },
  },
  {
    key: "finance", slug: "markets-and-filings", eyebrow: "Reference corpus",
    serpTitle: "Company filings and earnings call search — askFinz",
    title: "What listed companies file, and what they said about it",
    seoTitle: "Company filings and earnings calls",
    lede: "Regulatory filings kept beside the earnings calls that explained them, with the reporting period attached to both.",
    unit: "filings",
    variables: ["Filer", "Reporting period", "Segment breakout", "Document type"],
    coverage: "Read from the regulators and exchanges that publish them, so a filing carries its period and its filer rather than arriving as a loose document.",
    capabilities: [
      {
        title: "Read the numbers and the explanation together",
        body: "The filed figures beside what management said about them on the call.",
        detail: ["Period on every document", "Filer never guessed", "Segment structure kept"],
      },
      { title: "Never compare two quarters as one", body: "The reporting period travels with the document." },
      { title: "Link a statement to the line it refers to", body: "A remark in a transcript can be traced to the figure behind it." },
      { title: "See the segment detail", body: "Where a filer breaks out divisions, that structure is preserved." },
    ],
    askIntro:
      "Filings and calls are two halves of one story and are usually read apart. Keeping the reporting period on both is what lets them be read together without comparing the wrong quarters.",
    questions: [
      "What did management say on the call about the segment whose numbers fell?",
      "Which reporting period does this figure belong to, and what is the comparable one?",
      "How does this filer break out its divisions, and has that structure changed?",
      "Where in the filing is the line a remark in the transcript is referring to?",
    ],
    returns: [
      { title: "The filed document itself", body: "As published, with its filer and its period, rather than a loose extract of it." },
      { title: "The call beside the filing", body: "What was said about a number, next to the number, for the same period." },
      { title: "A segment view that survives", body: "Where a filer breaks out divisions, that structure is preserved rather than flattened." },
    ],
    limits: [
      "It is not market data. Filings and transcripts are documents; prices, quotes and live tickers are a different thing and are not on this shelf.",
      "It is not advice. Nothing here is a recommendation, and a filing says what a company chose to say.",
    ],
    workflow: [
      { title: "Ask about the company, not the form number", body: "Describe what you want to know; which document holds it is the part you should not have to know." },
      { title: "Pin the period", body: "The period travels with the document, so two quarters never quietly get read as one." },
      { title: "Open it in Finance", body: "The Finance workspace reads this collection, so a company view carries the filings underneath it." },
    ],
    relatedApp: { slug: "finance", name: "Finance", note: "The Finance workspace reads this collection, so a company view carries the filings underneath it." },
  },
  {
    key: "medical",
    /* trial / observational / evidence-synthesis are study designs. */
    kindTitle: "By study type",
    slug: "medicine-and-clinical-research", eyebrow: "Reference corpus",
    serpTitle: "Clinical trials search: studies and literature — askFinz",
    title: "Clinical knowledge, and the trials behind it",
    lede: "Registered studies kept as registry records — with phase, sponsor and recruiting status intact — beside the literature.",
    unit: "records",
    variables: ["Trial phase", "Sponsor", "Recruiting status", "Enrolled population"],
    coverage: "Read from trial registries and medical publishers directly, so a study's registered design is distinguishable from the paper written afterwards.",
    capabilities: [
      {
        title: "Find the trial, not the article about it",
        body: "The registered study itself, rather than coverage of it.",
        detail: ["Phase and sponsor", "Recruiting status", "Registry vs paper kept apart"],
      },
      { title: "See whether it is recruiting", body: "Phase, sponsor and status decide whether an answer is useful today." },
      { title: "Read results against what was pre-registered", body: "The registry record and the published paper are kept as separate things." },
      { title: "Check the population", body: "Who a study enrolled is usually the question behind the question." },
    ],
    askIntro:
      "The registry record and the paper written afterwards are kept as two different things, which is what makes it possible to ask whether a study reported what it set out to measure.",
    questions: [
      "Which trials for this condition are still recruiting, and where?",
      "What phase is this study at, and who is sponsoring it?",
      "Did the published paper report the outcome the trial pre-registered?",
      "Who was actually enrolled — which ages, which populations, how many?",
    ],
    returns: [
      { title: "The registry record", body: "Phase, sponsor, status and enrolled population as registered, not as summarised by a news story." },
      { title: "A list you can filter", body: "Narrow by study type, by status or by who is running it." },
      { title: "The literature beside it", body: "The trial record and the papers around it read from the same shelf." },
    ],
    limits: [
      "It is not medical advice and it is not a clinical decision tool.",
      "A registry record is what a sponsor registered. It is not proof a study ran as described, or that it ever finished.",
    ],
    workflow: [
      { title: "Ask about the condition or the intervention", body: "Plain language is enough — you should not need the registration number to find the study." },
      { title: "Check status before anything else", body: "Phase, sponsor and recruiting status decide whether an answer is any use today." },
      { title: "Read the paper against the registration", body: "The two records stay separate, so what was measured and what was reported can be compared." },
    ],
    relatedApp: { slug: "clinical-trials", name: "Clinical trials", href: "https://clinical-trials.askfinz.ai", note: "The clinical-trials reference page reads this collection; the Med workspace is on the roadmap and will read it too." },
  },
  {
    key: "shopping", slug: "products-and-reviews", eyebrow: "Reference corpus",
    serpTitle: "Product and review search — askFinz",
    title: "Products matched across retailers, with what owners said",
    seoTitle: "Product and review index",
    lede: "The same item recognised across shops, so you compare a product rather than ten pages selling it.",
    unit: "products",
    variables: ["Model identifier", "Retailer carrying the item", "Recurring review themes"],
    coverage: "Read from retailer listings and the reviews published against them, matched on model identifiers rather than on product titles.",
    capabilities: [
      {
        title: "Compare an item, not ten listings",
        body: "Duplicate listings collapse into the single product they describe.",
        detail: ["Matched on model number", "Variants stay separate", "Retailers listed together"],
      },
      { title: "Read what reviewers keep raising", body: "Recurring themes rather than an averaged star rating." },
      { title: "Keep the model number attached", body: "A near-identical variant is not quietly treated as the same thing." },
      { title: "See where it is actually sold", body: "Which retailers carry it, without a page per retailer." },
    ],
    askIntro:
      "Matching is done on the model identifier rather than the product title, which is the difference between comparing one item and comparing ten pages that are trying to sell it to you.",
    questions: [
      "Which retailers carry this exact model, rather than one that looks like it?",
      "What do owners keep raising about this after a few months of use?",
      "How does this variant differ from the one with the near-identical name?",
      "Which products in this category are the same item under different branding?",
    ],
    returns: [
      { title: "One product, several sellers", body: "The item, with the retailers carrying it, instead of a result per shop." },
      { title: "Themes rather than a star average", body: "What reviewers keep coming back to, which an averaged rating hides." },
      { title: "Variants held apart", body: "The model identifier is kept, so a near-identical model is not silently merged in." },
    ],
    limits: [
      "Prices move faster than any index. Treat what is here as what a listing said when it was read, and check the shop before you buy.",
      "It is not a buying recommendation, and reviews are what reviewers wrote — nothing here verifies them.",
    ],
    workflow: [
      { title: "Describe the thing you want", body: "Not the product name, which you may not have — what it has to do and what it has to fit." },
      { title: "Compare the item, once", body: "Duplicate listings collapse into the product, so the comparison is between things and not between pages." },
      { title: "Assemble it in Shop", body: "Shop reads this collection when building a cart, which is how it compares products rather than listings." },
    ],
    relatedApp: { slug: "shop", name: "Shop", note: "Shop reads this collection when assembling a cart, which is how it compares products rather than listings." },
  },
  {
    key: "estate", slug: "property-and-construction", eyebrow: "Reference corpus",
    serpTitle: "Property search: for sale, to let and being built — askFinz",
    title: "What is for sale, to let, and being built",
    lede: "Live property listings and construction records, kept with the practical detail a decision actually turns on.",
    unit: "listings",
    variables: ["Price", "Availability", "Location", "Associated planning record"],
    coverage: "Read from agents, portals and public planning records, so a listing reflects what is on the market now rather than a guide written last year.",
    capabilities: [
      {
        title: "Work from live listings",
        body: "What is available now, not a written-up description of an area.",
        detail: ["Price and availability kept", "Precise location", "Planning read alongside"],
      },
      { title: "Keep price and availability attached", body: "The detail that decides a shortlist travels with the record." },
      { title: "See the location precisely", body: "Where a property actually is, not the district it is marketed under." },
      { title: "Read planning alongside listings", body: "What is being built nearby is part of the same question." },
    ],
    askIntro:
      "Listings and planning records are usually two separate searches. Held on one shelf, what is for sale and what is about to be built next to it become one question.",
    questions: [
      "What is on the market in this area right now, in this price range?",
      "What has been granted planning permission near a property I am looking at?",
      "Which listings actually sit where they claim, rather than in the district they are marketed under?",
      "What is being built here, and by whom?",
    ],
    returns: [
      { title: "A shortlist you can narrow", body: "Price, availability and location as fields, so filtering is real rather than reading listings one at a time." },
      { title: "The listing as published", body: "The agent's own record, with the practical detail intact." },
      { title: "Planning beside the property", body: "Construction records read against listings, because they are one decision." },
    ],
    limits: [
      "A listing is not a valuation, and an asking price is not a sale price.",
      "Coverage follows the agents and public registers that publish openly, so it is uneven between areas.",
    ],
    workflow: [
      { title: "Say what you actually need", body: "Area, price, and the constraint that really decides it — not a portal's filter vocabulary." },
      { title: "Check what is happening nearby", body: "Planning records sit on the same shelf, so a development next door is not a surprise later." },
      { title: "Take it into Estate", body: "Estate is on the roadmap and will read this collection; the material is already held and searchable today." },
    ],
    relatedApp: { slug: "estate", name: "Estate", note: "Estate is on the roadmap and will read this collection; the material is already held and searchable today." },
  },
  {
    key: "trademarks",
    /* These are statuses on a register, not types. */
    kindTitle: "Status on the register",
    slug: "trademarks", eyebrow: "Reference corpus",
    title: "Registered marks, with their territory and status",
    seoTitle: "Registered trademark search",
    lede: "Trademark registrations read from the offices that grant them — searchable by what a mark covers, not just its spelling.",
    unit: "marks",
    variables: ["Registry office", "Goods and services claimed", "Territory", "Legal status"],
    coverage: "Read from national and regional registries, so a mark carries its office, its classes and its current legal status.",
    capabilities: [
      {
        title: "Search by what a mark covers",
        body: "The goods and services claimed, rather than the string alone.",
        detail: ["Classes, not just spelling", "Territory on every mark", "Live or lapsed"],
      },
      { title: "See the territory", body: "A mark registered elsewhere is not the same obstacle as one registered here." },
      { title: "Check whether it is live", body: "Lapsed and registered marks are different problems to have." },
      { title: "Read it beside the patents", body: "Marks and filings answer two halves of the same clearance question." },
    ],
    askIntro:
      "A mark is only an obstacle for particular goods, in a particular territory, while it is live. All three are kept, so the search can be about the actual problem rather than about a spelling.",
    questions: [
      "Has anyone registered this mark for the goods I want to sell?",
      "Is the mark that looks like an obstacle actually live, or has it lapsed?",
      "Which territories is this brand protected in, and which is it not?",
      "What classes does this registration really cover?",
    ],
    returns: [
      { title: "Marks filtered by class", body: "What is claimed, not every registration that happens to share a word." },
      { title: "Status and territory on each", body: "Which office granted it, where it applies, and whether it still stands." },
      { title: "The register entry to follow", body: "Every record points back at the office that granted it." },
    ],
    limits: [
      "It is not a clearance opinion. A search is where clearance starts; whether a mark is an obstacle is a legal judgement.",
      "Registers move. Status is as published, so a very recent filing or lapse may not have reached this shelf yet.",
    ],
    workflow: [
      { title: "Search the goods, not the word", body: "Describe what you will sell under the name; the classes are what decide whether a mark matters." },
      { title: "Filter to live marks in your territory", body: "Lapsed marks elsewhere are noise, and they are the bulk of what a plain name search returns." },
      { title: "Read it beside the filings", body: "The patents reference page covers filings; this collection covers the marks alongside them." },
    ],
    relatedApp: { slug: "patents", name: "Patents", href: "/collections/patents", note: "The patents reference page covers filings; this collection covers the marks alongside them." },
  },
  {
    key: "code", slug: "code-repositories", eyebrow: "Reference corpus",
    title: "Public repositories, read with their documentation",
    seoTitle: "Code repository search",
    lede: "Libraries findable by what they do rather than what they are called, with the docs read alongside the code.",
    unit: "repositories",
    variables: ["Licence", "Published documentation", "Maintenance activity"],
    coverage: "Read from public code hosts and the documentation sites projects publish, so intended use is read from the docs rather than inferred from source.",
    capabilities: [
      {
        title: "Find a library by behaviour",
        body: "Describe what you need; the package name is what you are missing.",
        detail: ["Docs read as text", "Licence carried", "Maintenance visible"],
      },
      { title: "Read the docs beside the code", body: "Intended use, not a guess from a function signature." },
      { title: "See whether it is still worked on", body: "The question people forget to ask until much later." },
      { title: "Check the licence", body: "Carried with the repository, because it decides whether you can use it at all." },
    ],
    askIntro:
      "Package names are the one thing you cannot search for when you do not know them. Reading the documentation alongside the repository is what makes a description enough.",
    questions: [
      "Which library does this, in this language, and is anyone still maintaining it?",
      "What licence is this package under, and can I use it in a commercial product?",
      "What did the project intend this for, in its own documentation?",
      "What are the alternatives to the dependency I am about to add?",
    ],
    returns: [
      { title: "Projects matching a description", body: "What a library does, found without knowing what it is called." },
      { title: "The documentation passage", body: "Intended use in the project's own words, rather than inferred from a function signature." },
      { title: "Licence and activity beside it", body: "The two facts that decide adoption, and the two people check last." },
    ],
    limits: [
      "It does not run anything. This is repositories and their documentation read as text — not a package registry, an installer or a build.",
      "Maintenance is read from what a project publishes. A quiet repository is not necessarily an abandoned one.",
    ],
    workflow: [
      { title: "Describe the behaviour you need", body: "In your own words. The name of the package is precisely what you are missing." },
      { title: "Read the docs before the source", body: "Intended use comes from the documentation, which is where a project says what it is for." },
      { title: "Ask it from the Code workspace", body: "Code reads this collection, so a question about an unfamiliar dependency is answered from its own documentation." },
    ],
    relatedApp: { slug: "code", name: "Code", note: "The Code workspace reads this collection, so a question about an unfamiliar dependency is answered from its own documentation." },
  },
  {
    key: "books", slug: "books-and-manuals", eyebrow: "Reference corpus",
    serpTitle: "Book and product manual search — askFinz",
    title: "Long-form works and the manuals nobody puts on a web page",
    seoTitle: "Books and product manuals",
    lede: "Product manuals and book-length texts read page by page — including the ones published only as documents.",
    unit: "documents",
    variables: ["Edition", "Section within the document", "Publisher or manufacturer"],
    coverage: "Read from publishers and manufacturers directly, including material published as PDFs that ordinary search never opens.",
    capabilities: [
      {
        title: "Answer the question the FAQ did not",
        body: "The manual itself, not a forum thread guessing at it.",
        detail: ["Documents opened and read", "Landed on the section", "Edition kept attached"],
      },
      { title: "Land on the section", body: "The specific answer usually exists in exactly one place in a long document." },
      { title: "Read documents, not just pages", body: "Manuals published as files are opened and read, scans included." },
      { title: "Keep the edition attached", body: "Instructions move between revisions more often than people expect." },
    ],
    askIntro:
      "Most of this material is published as documents rather than web pages, which is why ordinary search cannot answer from it. Read page by page, the one paragraph that answers the question becomes findable.",
    questions: [
      "What does the manual say to do when this code appears on the display?",
      "Which edition of this manual matches the model number on the back of the unit?",
      "Where in this long document is the section that actually answers this?",
      "What does the published text say, rather than what a forum thread thinks it says?",
    ],
    returns: [
      { title: "The passage, not the document", body: "The section that answers it, out of a document that may run to hundreds of pages." },
      { title: "Its edition and publisher", body: "So instructions from the wrong revision are recognisable as such." },
      { title: "A route back to the original", body: "Every record keeps the address it was read from." },
    ],
    limits: [
      "It is not every manual ever printed. What is here is what manufacturers and publishers put online.",
      "It is not a library to borrow from. These are documents read for their content, not copies to keep.",
    ],
    workflow: [
      { title: "Ask the practical question", body: "The error on the panel, the part that will not come off — not the document's title." },
      { title: "Check the edition", body: "Instructions move between revisions, so the edition is kept with the passage." },
      { title: "Get the answer without the download", body: "Search reaches into these documents directly, which is why a manual answer arrives without one." },
    ],
    relatedApp: { slug: "search", name: "Search", note: "Search reaches into these documents directly, which is why a manual answer arrives without a download." },
  },
  {
    key: "learning", slug: "course-material", eyebrow: "Reference corpus",
    serpTitle: "Course material and syllabus search — askFinz",
    title: "Syllabuses and course material, with the level attached",
    seoTitle: "Course material and syllabuses",
    lede: "What a programme actually teaches, read from the institution rather than a directory's summary of it.",
    unit: "courses",
    variables: ["Institution", "Level the material is pitched at", "Syllabus content"],
    coverage: "Read from the institutions publishing them, so a syllabus arrives with the level it is pitched at.",
    capabilities: [
      {
        title: "See what is really covered",
        body: "The syllabus itself, before committing time or money.",
        detail: ["Institution's own material", "Level attached", "Comparable structure"],
      },
      { title: "Tell an introduction from a graduate module", body: "Level is the difference between useful and wasted." },
      { title: "Read the institution's own material", body: "Not an aggregator's paraphrase of it." },
      { title: "Compare programmes fairly", body: "Same structure, so two courses can be read side by side." },
    ],
    askIntro:
      "Course directories describe programmes; institutions publish syllabuses. Reading the second is the only way to find out what is actually taught before committing to it.",
    questions: [
      "What does this course actually cover, week by week?",
      "Is this module an introduction, or does it assume a degree already?",
      "Which institutions teach this subject at the level I need?",
      "How do two programmes compare on what they teach, rather than on how they market themselves?",
    ],
    returns: [
      { title: "The syllabus as published", body: "The institution's own material rather than a directory's paraphrase of it." },
      { title: "The level it is pitched at", body: "Which is the difference between a useful term and a wasted one." },
      { title: "Two programmes side by side", body: "Held in the same structure, so a comparison is between contents and not between prospectuses." },
    ],
    limits: [
      "It is not admissions guidance, and it does not reliably hold fees, deadlines or entry requirements.",
      "A syllabus is a plan. What is taught in a given year can differ from what was published.",
    ],
    workflow: [
      { title: "Ask what you want to learn", body: "Not the course code, which is the thing you are trying to find." },
      { title: "Filter by level first", body: "The same subject at two levels is two different commitments." },
      { title: "Bring it into Learn", body: "Learn reads this collection alongside your own imported course material." },
    ],
    relatedApp: { slug: "learn", name: "Learn", note: "Learn reads this collection alongside your own imported course material." },
  },
  {
    key: "discussions", slug: "questions-and-answers", eyebrow: "Reference corpus",
    serpTitle: "Questions and answers search — askFinz",
    title: "Questions people really asked, and what actually worked",
    seoTitle: "Questions and answers index",
    lede: "Threads read for their resolution — the accepted answer rather than the longest argument.",
    unit: "threads",
    variables: ["Accepted answer", "Software version the fix applied to", "Thread date"],
    coverage: "Read from public question-and-answer sites and forums, with the environment and version a fix applied to kept attached.",
    capabilities: [
      {
        title: "Skip to what resolved it",
        body: "The accepted answer leads, which is rarely the longest reply.",
        detail: ["Resolution first", "Version kept", "Date always visible"],
      },
      { title: "Match the error, not the words", body: "Find the thread that is genuinely the same problem." },
      { title: "Keep the version attached", body: "A fix from an older release is recognisable as one." },
      { title: "See whether it is still current", body: "Advice ages; the date travels with the thread." },
    ],
    askIntro:
      "A thread is only useful if it is the same problem, on a version close enough to yours, recently enough to still be true. Those three are kept, so the answer can be judged before it is followed.",
    questions: [
      "Has anyone hit this exact error message, and what fixed it?",
      "Does the accepted fix still apply to the version I am running?",
      "What did the people who solved this actually change, as opposed to what they tried?",
      "Is this advice current, or is it three major releases out of date?",
    ],
    returns: [
      { title: "The resolution first", body: "The accepted answer, which is very rarely the longest reply on the thread." },
      { title: "The version it applied to", body: "So a confident fix from an older release is recognisable as one." },
      { title: "Threads that are the same problem", body: "Matched on the error rather than on the words around it." },
    ],
    limits: [
      "It is people, not documentation. An accepted answer is what worked for someone, not what a maintainer says is correct.",
      "Threads age badly. The date and version are kept precisely because a confident old answer is the most dangerous kind.",
    ],
    workflow: [
      { title: "Paste the problem, not a summary", body: "The error text is the most searchable thing you have, and the least ambiguous." },
      { title: "Check the version before the fix", body: "It is on the thread, so an answer from three releases back does not get applied by accident." },
      { title: "Ask it from Code", body: "Code reads this collection when an error message is more useful than documentation." },
    ],
    relatedApp: { slug: "code", name: "Code", note: "Code reads this collection when an error message is more useful than documentation." },
  },
  {
    key: "jobs", slug: "jobs", eyebrow: "Reference corpus",
    serpTitle: "Job search: openings as employers publish them — askFinz",
    title: "Openings as the employer published them",
    lede: "Roles read from the employer's own posting rather than an aggregator's copy, which is often stale or altered.",
    unit: "postings",
    variables: ["Employer", "Whether the role is still open", "Work described"],
    coverage: "Read from employers' own careers pages, so a posting reflects what was actually written and whether it is still open.",
    capabilities: [
      {
        title: "Read the employer's own words",
        body: "Aggregator copies are frequently edited or out of date.",
        detail: ["Straight from careers pages", "Open or closed", "Searched by the work"],
      },
      { title: "See whether it is still open", body: "A closed role is worse than no result." },
      { title: "Search by the work, not the title", body: "Job titles are the least standardised field there is." },
      { title: "Read it beside procurement", body: "Who is hiring and who is buying answer different halves of one question." },
    ],
    askIntro:
      "Reading the employer's own posting rather than an aggregator's copy is the whole point: the copy is the version that gets edited, truncated and left up after the role has closed.",
    questions: [
      "Which employers are hiring for this work, in their own words?",
      "Is this posting still open, or has an aggregator left it up?",
      "What does this role actually involve, when the title tells me nothing?",
      "Which organisations are hiring and buying in the same area at the same time?",
    ],
    returns: [
      { title: "The posting as written", body: "The employer's own text, not a truncated copy of it." },
      { title: "Whether it is still open", body: "Because a closed role is worse than no result at all." },
      { title: "Roles matched on the work", body: "Job titles are the least standardised field there is; the description is not." },
    ],
    limits: [
      "It is not an application service. You will find the employer's posting; applying happens on their site.",
      "It follows employers who publish their own careers pages, so it is not every vacancy in a market.",
    ],
    workflow: [
      { title: "Describe the work", body: "Titles are invented locally. What the job involves is the searchable part." },
      { title: "Filter out what has closed", body: "Whether a posting is still open is kept, which is the failure mode of every aggregator." },
      { title: "Pair it with procurement", body: "The tenders reference page covers public buying, which pairs naturally with hiring signals." },
    ],
    relatedApp: { slug: "tenders", name: "Tenders", href: "/collections/public-tenders", note: "The tenders reference page covers public procurement, which pairs naturally with hiring signals." },
  },
  {
    key: "travel", slug: "travel", eyebrow: "Reference corpus",
    serpTitle: "Travel listing search — askFinz",
    title: "Getting there and staying there, from live listings",
    seoTitle: "Travel listings index",
    lede: "Routes, stays and activities read from operators, with dates and availability kept as structured detail.",
    unit: "listings",
    variables: ["Operator", "Dates", "Price", "Availability"],
    coverage: "Read from operators and the places themselves, so availability reflects now rather than a guide written a season ago.",
    capabilities: [
      {
        title: "Plan against what exists",
        body: "Live listings rather than prose about a destination.",
        detail: ["Dates and prices kept", "Operator named", "Route and stay together"],
      },
      { title: "Keep dates and prices attached", body: "The practical detail is the answer, not a footnote to it." },
      { title: "See the operator", body: "Who actually runs a thing decides whether the listing is reliable." },
      { title: "Read place and route together", body: "Getting there and staying there are one decision." },
    ],
    askIntro:
      "Travel writing describes places; listings say what is actually running. Keeping dates, prices and the operator as fields is what turns a description into something you can plan against.",
    questions: [
      "What is actually available on these dates, rather than what a guide recommends?",
      "Who operates this — the operator itself, or a reseller?",
      "What does getting there and staying there look like as one decision?",
      "What is open at this time of year in this place?",
    ],
    returns: [
      { title: "Listings you can constrain", body: "Dates, price and availability as fields, so a plan can be filtered rather than read." },
      { title: "The operator named", body: "Who runs a thing decides how much a listing is worth." },
      { title: "Route and stay on one shelf", body: "Because they are one decision, and they are usually two searches." },
    ],
    limits: [
      "This page is not a booking system, and availability changes by the hour.",
      "It is not a review site. What a place is like is a different question from whether it exists and is open.",
    ],
    workflow: [
      { title: "Say the dates and the constraint", body: "What has to be true — when, where, and the thing that would rule a plan out." },
      { title: "Work from listings, not prose", body: "Availability reflects now, rather than a guide written a season ago." },
      { title: "Build the itinerary in Travel", body: "The Travel workspace reads this collection when assembling one." },
    ],
    relatedApp: { slug: "travel", name: "Travel", note: "The Travel workspace reads this collection when building an itinerary." },
  },
  {
    key: "media", slug: "film-music-and-video", eyebrow: "Reference corpus",
    serpTitle: "Film, music and video search — askFinz",
    title: "Titles keyed to the identifier each industry uses",
    seoTitle: "Film, music and video catalogue",
    lede: "Releases matched on their industry identifier, so a remaster, a reissue and a same-titled remake stay separate things.",
    unit: "titles",
    variables: ["Industry identifier", "Release year", "Territory", "Edition"],
    coverage: "Read from the catalogues and registries each industry maintains, so a release carries its own identifier rather than being matched on its name.",
    capabilities: [
      {
        title: "Find the exact release",
        body: "Title matching cannot tell a remaster from a remake.",
        detail: ["Industry identifier kept", "Year and territory", "Editions held apart"],
      },
      { title: "Keep year and territory attached", body: "The same title ships differently in different markets." },
      { title: "Separate editions", body: "A reissue is not the original, and both are held." },
      { title: "Search across formats", body: "Film, music and video answered from one shelf." },
    ],
    askIntro:
      "Titles repeat, and a title search cannot tell a remaster from a remake. Because each release keeps the identifier its own industry assigns, the distinction survives.",
    questions: [
      "Which release is this exactly — the original, the remaster or the reissue?",
      "Was this title released in this territory, and in what year?",
      "Which recordings share a catalogue identifier across formats?",
      "How do two same-named works differ, when a title search cannot separate them?",
    ],
    returns: [
      { title: "One release, precisely", body: "The edition you meant, rather than everything that shares a name with it." },
      { title: "Year, territory and edition", body: "The three fields a title alone can never carry." },
      { title: "Film, music and video together", body: "One shelf, so a cross-format question does not become three searches." },
    ],
    limits: [
      "It is not a streaming catalogue. Where you can watch or listen to something is a different question from what the release is.",
      "It holds catalogue records, not the works themselves.",
    ],
    workflow: [
      { title: "Name the work, however roughly", body: "Ambiguity is expected — the identifier is what resolves it afterwards." },
      { title: "Pin the edition", body: "Year, territory and edition are kept, so a reissue is not silently substituted for the original." },
      { title: "Use it in Stream", body: "Stream reads this collection for recommendations and for matching what a creator uploads." },
    ],
    relatedApp: { slug: "stream", name: "Stream", note: "Stream reads this collection for recommendations and for matching what a creator uploads." },
  },
  {
    key: "lifestyle", slug: "events-and-recipes", eyebrow: "Reference corpus",
    serpTitle: "Event and recipe search — askFinz",
    title: "What is on, and what it actually involves",
    lede: "Events and recipes held with their quantities, timings and places intact rather than summarised away.",
    unit: "entries",
    variables: ["Quantities", "Timings", "Date", "Place", "Organiser"],
    coverage: "Read from organisers and publishers directly, so a listing keeps the detail that decides whether it is worth your evening.",
    capabilities: [
      {
        title: "Keep the practical detail",
        body: "Quantities, timings, dates and places as structured fields.",
        detail: ["Fields, not prose", "One-off vs series", "Organiser's own words"],
      },
      { title: "Tell a one-off from a series", body: "Listings routinely blur the two." },
      { title: "Search by constraint", body: "What you have, how long you have, where you are." },
      { title: "Read the source listing", body: "The organiser's own words, not a directory's rewrite." },
    ],
    askIntro:
      "The detail is the answer here. Quantities, timings and places are kept as fields rather than summarised into a description, which is what lets a search run on constraints.",
    questions: [
      "What is on near me on a given evening, and what does it actually involve?",
      "What can I cook with what is in the cupboard and forty minutes?",
      "Is this a one-off, or part of a series that runs all year?",
      "Who is organising this, and where exactly is it?",
    ],
    returns: [
      { title: "A list filtered by constraint", body: "What you have, how long you have, and where you are." },
      { title: "The practical fields intact", body: "Quantities and timings kept rather than compressed into a paragraph." },
      { title: "The organiser's own listing", body: "Not a directory's rewrite of it." },
    ],
    limits: [
      "It is not a ticketing service, and a listing is not a booking.",
      "Local coverage is uneven — it is only as good as what organisers publish openly.",
    ],
    workflow: [
      { title: "Give the constraint, not the category", body: "An evening, a budget, an ingredient list — the things that actually rule options out." },
      { title: "Read the detail before deciding", body: "Timings and quantities are kept, which is the part a summary always loses." },
      { title: "Ask it from Search", body: "Search answers from this collection when a question is practical rather than informational." },
    ],
    relatedApp: { slug: "search", name: "Search", note: "Search answers from this collection when a question is practical rather than informational." },
  },
  {
    key: "agriculture", slug: "agriculture", eyebrow: "Reference corpus",
    serpTitle: "Agriculture data search: food and farming — askFinz",
    title: "Food production, read from the bodies that record it",
    seoTitle: "Agriculture and food production data",
    lede: "Agricultural material held as records — crops, yields, practice and the regulation around them.",
    unit: "records",
    variables: ["Publishing body", "Season or period", "Production figure", "Applicable regulation"],
    coverage: "Read from agricultural agencies, research bodies and the organisations publishing production data.",
    capabilities: [
      {
        title: "Read production as data",
        body: "Records rather than articles describing records.",
        detail: ["Season always attached", "Publishing body named", "Rule beside the practice"],
      },
      { title: "Keep the season attached", body: "Agricultural figures are meaningless without their period." },
      { title: "See the practice beside the rule", body: "What is done and what is required, together." },
      { title: "Trace to the publishing body", body: "Who recorded a figure decides how much weight it carries." },
    ],
    askIntro:
      "A production figure without its period and its publisher is not information. Both are kept, which is what makes this shelf usable for anything beyond a headline number.",
    questions: [
      "What were the recorded yields for this crop in this season?",
      "Which body published this production figure, and how do they define the measure?",
      "What regulation applies to this practice, and where is it written down?",
      "How does recorded practice differ from what the rules require?",
    ],
    returns: [
      { title: "Figures with their period", body: "Because an agricultural number without its season is not a number." },
      { title: "The publishing body named", body: "Who recorded a figure decides how much weight it carries." },
      { title: "Rule and practice together", body: "What is required and what is done, read from one shelf." },
    ],
    limits: [
      "It is not a live commodity feed. These are published records, with the period they belong to attached.",
      "It is not farm advice. The figures and the rules are here; applying them to a holding is not.",
    ],
    workflow: [
      { title: "Ask about the crop and the period", body: "Both, always — a figure without its season cannot be compared to anything." },
      { title: "Check who published it", body: "Definitions of the same measure differ between bodies, so the body is kept with the figure." },
      { title: "Take it into Research", body: "Research reads this collection when a question crosses from policy into production." },
    ],
    relatedApp: { slug: "research", name: "Research", note: "Research reads this collection when a question crosses from policy into production." },
  },

  /* ══════════════════════════════════════════════════════════════════════════════════════════
     THE FOUR PORTED FROM /apps.

     patents, standards, tenders and law each had a hand-written page under /apps. Two of those
     pages — /apps/case-law and /apps/legislation — rendered the SAME corpus with the same live
     figures, which is why `law` below merges their copy into one entry rather than picking a
     winner. The routes are now permanent redirects; the copy is ported as written.
     ══════════════════════════════════════════════════════════════════════════════════════════ */
  {
    key: "patents",
    slug: "patents",
    serpTitle: "Patent search: patents and published applications — askFinz",
    eyebrow: "Reference corpus",
    title: "Patents, read from the offices that grant them",
    seoTitle: "Patents and published applications",
    lede: "Granted patents and published applications from more than a hundred patent offices — searchable by what an invention does, not just the words in its title.",
    unit: "publications",
    variables: ["Publication office", "Filing and grant dates", "Applicant and inventor", "Classification", "Abstract and claims"],
    coverage: "Read directly from the patent offices' own published data, so a record says what the office says. English titles and abstracts, with the full specification available for the documents that publish one.",
    capabilities: [
      {
        title: "Search by what it does",
        body: "Describe the invention in your own words and find the filings that match, even when they use completely different terminology.",
        detail: ["Plain-language description", "Office and dates kept", "Abstract and claims read"],
      },
      { title: "See the landscape", body: "Who is filing, in which offices, and how a field has moved year on year." },
      { title: "Follow it into the record", body: "Every result links back to the office's own publication, so a claim can be checked at source." },
      { title: "Read alongside the research", body: "Patents sit next to the academic corpus, so an invention and the papers behind it turn up in the same search." },
    ],
    askIntro: "Patent language is written to be broad rather than searchable, and two filings for the same idea rarely share a word. Reading the abstract and claims is what makes a plain description enough to find them.",
    questions: [
      "Which companies filed on solid-state batteries in Japan last year?",
      "Has anyone already filed on the mechanism I am about to describe?",
      "How has filing activity in this field moved over the last decade, and in which offices?",
      "Which filings name this applicant as the inventor rather than the assignee?",
    ],
    returns: [
      { title: "Filings that match a description", body: "Found on what the invention does, not on the words its drafter happened to choose." },
      { title: "A set you can group", body: "By office, by applicant or by year, so a field can be read as a landscape rather than one document at a time." },
      { title: "A link into the register", body: "Every result points back at the office's own publication, so a claim can be checked at source." },
    ],
    limits: [
      "It is not a freedom-to-operate opinion. A search is where clearance starts; whether a claim reads on your product is a legal judgement.",
      "Filings publish on a delay set by the office, so the most recent months of activity in any field are necessarily incomplete.",
    ],
    workflow: [
      { title: "Describe the invention", body: "In your own words. The terminology a drafter used is exactly what you do not have." },
      { title: "Read the claims, not the title", body: "Titles are deliberately uninformative; the claims are where the scope actually is." },
      { title: "Set it beside the papers", body: "The research workspace reads patents alongside papers, standards and datasets, with citations that link back to the original filing." },
    ],
    relatedApp: { slug: "research", name: "Research", note: "The research workspace reads patents alongside papers, standards and datasets, with citations that link back to the original filing." },
  },
  {
    key: "standards",
    slug: "standards-and-specifications",
    serpTitle: "Technical standards and specifications search — askFinz",
    eyebrow: "Reference corpus",
    title: "The specifications the internet runs on",
    seoTitle: "Technical standards and specifications",
    lede: "Technical standards in full — the documents that define how systems actually interoperate.",
    unit: "specifications",
    variables: ["Issuing body", "Designation and revision", "Status", "Scope statement", "Subject area"],
    coverage: "Read from the standards bodies' own publication services, including the complete text of each specification rather than an abstract.",
    capabilities: [
      {
        title: "Search the actual spec",
        body: "Ask how something is supposed to behave and land on the paragraph that defines it.",
        detail: ["Full text, not abstracts", "Designation and revision", "Issuing body named"],
      },
      { title: "Read the whole document", body: "Full text, not a summary — the detail is the point." },
      { title: "Check behaviour against the definition", body: "Useful the moment an implementation and a specification disagree." },
      { title: "Sits beside your code", body: "Available in the workspaces where the question usually comes up." },
    ],
    askIntro: "A specification answers in one paragraph out of hundreds of pages. Holding the complete text rather than an abstract is the only reason that paragraph can be found at all.",
    questions: [
      "What does the specification actually require here, in the normative text?",
      "Which revision introduced this behaviour, and which one am I reading?",
      "Is this field mandatory, or is it optional with a default?",
      "Which body issued this document, and has it been superseded?",
    ],
    returns: [
      { title: "The clause, not the document", body: "The paragraph that defines the behaviour, out of a specification that may run to hundreds of pages." },
      { title: "Its designation and revision", body: "So the answer can be quoted with the document and version it came from." },
      { title: "The issuing body attached", body: "Because two bodies can define the same term differently and both be right." },
    ],
    limits: [
      "It is not a conformance test. It will tell you what a specification requires; whether an implementation meets it is a separate exercise.",
      "Where a body publishes only an abstract or a paid document, that is what is held — the full normative text is not there to read.",
    ],
    workflow: [
      { title: "Ask how it is supposed to behave", body: "Describe the behaviour in dispute rather than searching for a document number." },
      { title: "Land on the defining clause", body: "The answer is normally in exactly one paragraph, and that is what comes back." },
      { title: "Keep it open while you work", body: "The code workspace can read a specification alongside you, so the definition is one question away." },
    ],
    relatedApp: { slug: "code", name: "Code", note: "The code workspace can read a specification while you work, so the definition is one question away." },
  },
  {
    key: "tenders",
    slug: "public-tenders",
    serpTitle: "Public tender search: procurement notices — askFinz",
    eyebrow: "Reference corpus",
    title: "Public procurement, as published",
    lede: "Contract notices from public buyers — who is buying what, where, and by when.",
    unit: "notices",
    variables: ["Buying authority", "Country", "Deadline", "Contract value band", "Subject of the contract"],
    coverage: "Read from the official procurement journal, covering public buyers across member states, with the full notice available for each record.",
    capabilities: [
      {
        title: "Find relevant contracts",
        body: "Describe what you supply and find the notices that match, across languages and buyer terminology.",
        detail: ["Matched on the subject", "Buyer and country kept", "Deadline on every notice"],
      },
      { title: "See the buyer", body: "Contracting authority and country on every notice." },
      { title: "Read the full notice", body: "The published document itself, including the detail that decides whether it is worth bidding." },
      { title: "Track a market", body: "New notices as they are published, so nothing is found a week late." },
    ],
    askIntro: "Procurement notices are written in the buyer's language and the buyer's vocabulary. Matching on the subject of the contract rather than on its wording is what makes them findable by a supplier.",
    questions: [
      "Which public bodies are buying what I supply, and by when do they need it?",
      "What is the value band on this contract, and which authority is behind it?",
      "Which notices in this category closed without an award being published?",
      "Who buys this across borders, in languages I do not read?",
    ],
    returns: [
      { title: "Notices matched to what you sell", body: "Found on the subject of the contract rather than on the buyer's phrasing of it." },
      { title: "A list you can sort by deadline", body: "Because the date a bid closes is the only field that decides whether the rest matters." },
      { title: "The published notice itself", body: "Including the detail that decides whether it is worth bidding at all." },
    ],
    limits: [
      "It is not a bidding platform. You will find the notice; submitting a tender happens through the buyer's own portal.",
      "It covers what public buyers publish openly, so below-threshold and privately-run procurement is largely outside it.",
    ],
    workflow: [
      { title: "Describe what you supply", body: "In your own words, not in the buyer's classification vocabulary." },
      { title: "Filter by deadline and buyer", body: "Both are kept on every notice, which is what turns a feed into a shortlist." },
      { title: "Read it as a dataset", body: "The data workspace, on the roadmap, will read procurement notices as records — filter, group and export rather than reading one notice at a time." },
    ],
    relatedApp: { slug: "data", name: "Data", note: "The data workspace, on the roadmap, will read procurement notices as a dataset — filter, group and export rather than reading one notice at a time." },
  },
  {
    /* ONE ENTRY, TWO OLD PAGES. /apps/case-law and /apps/legislation both rendered this corpus and
       both showed the same live count, because the index files judgments and instruments on one
       shelf pair. Merging their copy is what makes the count mean something: two URLs claiming the
       same number is how a reader concludes neither is real. */
    key: "law",
    slug: "law-and-legislation",
    serpTitle: "Case law and legislation search — askFinz",
    eyebrow: "Reference corpus",
    title: "Judgments and legislation, from the bodies that publish them",
    seoTitle: "Case law and legislation",
    lede: "Judgments from national and supranational courts, and the acts, regulations and statutory instruments beside them — searchable by what a case decided or what a rule requires, not only by name.",
    unit: "documents",
    variables: ["Court", "Jurisdiction", "Decision date", "Parties", "Judgment text", "Instrument type", "In-force dates", "Amending history", "Provision text"],
    coverage: "Read from court and national reporting services, and from each government's own publication service. Judgments carry their formal citation, so the same case arriving from two publishers is recognised as one case rather than two. Where an instrument is published in several languages, the English text is used, so one law is one document rather than twenty-four.",
    capabilities: [
      {
        title: "Search by the point of law",
        body: "Describe the issue and find the judgments that turned on it, without knowing the case name.",
        detail: ["Court and jurisdiction", "Decision date kept", "Formal citation attached"],
      },
      { title: "Find the rule, not an article about it", body: "Search the actual instrument, and land on the section that answers the question." },
      { title: "Read the judgment itself", body: "The full text where the court publishes one — not a headnote written by someone else." },
      { title: "Compare across borders", body: "The same question asked of UK, US and EU material in a single search." },
    ],
    askIntro: "Case names are the hardest way to find a judgment and the only route most systems offer, and an obligation lives in one provision that almost every search answers with an article summarising it instead. Holding the judgment and the instrument themselves is what lets the answer be the case and the section.",
    questions: [
      "Which judgments have turned on this issue, when I do not know a single case name?",
      "What does the instrument itself require here, rather than what a summary says it requires?",
      "When did this provision come into force, and has it since been amended?",
      "Which court decided this, in which jurisdiction, and when?",
      "How is the same obligation written in another jurisdiction?",
    ],
    returns: [
      { title: "Judgments on the point", body: "Found by the issue that was argued rather than by the name of the parties." },
      { title: "The provision, not a summary", body: "The section that answers the question, from the instrument as its own publisher issued it." },
      { title: "Court, jurisdiction and date", body: "On every judgment, so authority is obvious before you read a word of it." },
      { title: "The citation to quote", body: "Cases and instruments carry their official reference, so a point can be cited and checked." },
    ],
    limits: [
      "It is not legal advice, and a judgment found is not a judgment that binds you.",
      "It cannot tell you whether a case is still good law. Nothing here tracks whether a decision has since been overruled or distinguished.",
      "Where an instrument is published in several languages, the English text is used — so a point turning on the wording of another language version is outside it.",
    ],
    workflow: [
      { title: "Describe the issue", body: "The point in dispute, in plain words — the case name and the instrument number are what you are trying to find." },
      { title: "Weigh the court, and check when it applied", body: "Court, jurisdiction and date decide what a judgment is worth; in-force dates decide which version of a rule mattered." },
      { title: "Read the two together", body: "The legal workspace, on the roadmap, will read case law beside legislation and your own matter files, with every claim linked back to the source." },
    ],
    relatedApp: { slug: "legal", name: "Legal", note: "The legal workspace, on the roadmap, will read case law beside legislation and your own contracts, so an obligation can be traced from clause to statute." },
  },

  /* ══════════════════════════════════════════════════════════════════════════════════════════
     THE SEVEN STORAGE-BACKED COLLECTIONS.

     Live and ingesting, and until now on no page at all, because they belong to no corpus:
     `getCorpusInsight()` returns null for every one of them. The storage feed carries them with a
     real count and a real per-record size, so that is what their pages show and that is ALL they
     show. No growth line, no facet bars, no coverage span, no field list — none of those exist to
     be read, and a plausible-looking one would be a fabrication on a page whose entire job is to
     be checkable.

     `key` is the collection's row in the storage feed. It is an internal store name: it selects
     the row and never reaches the page. The reader sees `title`.
     ══════════════════════════════════════════════════════════════════════════════════════════ */
  /* THE GENERAL WEB ITSELF, which was missing from this hub entirely.
     Every other entry here is a shelf of one KIND of record. This is the one that holds a page
     because it is a page — the crawl's own collection, and by a wide margin the largest thing in
     the index. Leaving it out made the hub read as though askFinz only holds speciality corpora,
     which understates it and is simply untrue.

     `key` is `askfinz_site_index`, NOT the quarter-stamped row name. The storage feed calls it
     `askfinz_site_index_h_2026q3`; the hub strips the `_h_YYYYqN` suffix before matching, so the
     stable stem is what belongs here. Hardcoding the quarter would blank this card in October. */
  {
    key: "askfinz_site_index",
    source: "storage",
    slug: "web-pages",
    serpTitle: "Web page search — askFinz",
    eyebrow: "Reference collection",
    title: "The open web, read page by page",
    seoTitle: "General web page index",
    lede: "Ordinary web pages, read and kept as pages. The collection everything else is filed out of — where a page belongs to no speciality shelf, it belongs here.",
    unit: "pages",
    record: "One page, kept with the address it was read from and when it was last read.",
    coverage: "The general crawl. A page that turns out to be a filing, a judgment or a standard is filed on that shelf instead; what remains here is the rest of the web.",
    capabilities: [
      {
        title: "Ask the web a question, not a search engine",
        body: "This is read into our own index rather than fetched from somebody else's results, so a question can be asked of the page itself.",
        detail: ["Read, not linked to", "Kept with its address", "Re-read as it changes"],
      },
      { title: "Documents are read too", body: "PDFs, Office files and slide decks are converted and read page by page, rather than returned as a filename to guess from." },
      { title: "Follow it back", body: "Every page keeps the address it came from, so an answer can be checked where it was published." },
    ],
    limits: [
      "It is not the whole web, and no index is. It holds what has been read so far.",
      "A page behind a login or a paywall is not in here. Where a site refuses the crawler, that refusal is respected rather than worked around.",
      "Freshness varies by page. Something that changes hourly and something that changed once in 2019 are not re-read on the same schedule.",
    ],
    relatedApp: { slug: "search", name: "Search", note: "Search reads this collection when a question does not belong to one of the speciality shelves." },
  },
  {
    key: "statistics",
    source: "storage",
    slug: "statistical-indicators",
    serpTitle: "Economic indicator search — askFinz",
    eyebrow: "Reference collection",
    title: "Statistical indicators, kept as records",
    seoTitle: "Economic and development indicators",
    lede: "Economic and development indicators held as records rather than as pages describing them — the measure itself, filed so it can be read alongside the others.",
    unit: "indicators",
    record: "One indicator, filed as a record of what was measured rather than as an article about the measurement.",
    coverage: "Filed as the kind of record it is, so an indicator sits with the rest of its collection rather than inside whichever page happened to quote it.",
    capabilities: [
      {
        title: "Reach the measure, not an article about it",
        body: "Ask what an indicator says and land on the record that holds it, instead of on the commentary written around it.",
        detail: ["Held as records", "Read as one set", "Traceable to where it was published"],
      },
      { title: "Read indicators side by side", body: "They are filed together, so more than one measure can be compared without opening a page for each." },
      { title: "Follow it back", body: "Every record keeps the address it was read from, so a figure can be checked where it was published." },
    ],
    limits: [
      "It is not a live market or price feed. These are published indicators, read as they were published.",
      "It does not reconcile definitions. Two bodies measuring the same thing may not mean the same thing by it, and nothing here resolves that for you.",
    ],
    relatedApp: { slug: "research", name: "Research", note: "Research reads this collection when a question needs the measure rather than the commentary around it." },
  },
  {
    key: "earth_observation",
    source: "storage",
    slug: "earth-observation",
    serpTitle: "Earth observation data search — askFinz",
    eyebrow: "Reference collection",
    title: "Earth observation, filed as records",
    seoTitle: "Earth observation records",
    lede: "Observations of the planet — its land, its atmosphere and its oceans — held as records rather than as articles reporting them.",
    unit: "records",
    record: "One observation, filed as the kind of record it is rather than as a page describing it.",
    coverage: "Filed alongside the research and agricultural collections, so an observation and the work built on it can be read from one place.",
    capabilities: [
      {
        title: "Find the observation itself",
        body: "Describe what you are looking for and reach the record, rather than the news story that summarised it.",
        detail: ["Held as records", "Filed beside the research", "Traceable to where it was published"],
      },
      { title: "Read it beside the science", body: "It sits next to the research and agriculture collections, so an observation and the papers around it turn up together." },
      { title: "Follow it back", body: "Every record keeps the address it was read from, so an observation can be checked at source." },
    ],
    limits: [
      "It is not imagery, and it is not a mapping service. These are records of observations, not the pictures behind them.",
      "It is not a monitoring alert. Records are read as they are published; nothing here watches for a threshold on your behalf.",
    ],
    relatedApp: { slug: "research", name: "Research", note: "Research reads this collection when a question crosses from the literature into what was actually measured." },
  },
  {
    key: "vulnerabilities",
    source: "storage",
    slug: "software-vulnerabilities",
    serpTitle: "Software vulnerability (CVE) search — askFinz",
    eyebrow: "Reference collection",
    title: "Software vulnerabilities, as disclosed",
    seoTitle: "Software vulnerability records",
    lede: "Disclosed weaknesses in software and the systems built on it, held as records — the disclosure itself, not the coverage of it.",
    unit: "disclosures",
    record: "One disclosed weakness, filed as a record rather than as an article reporting it.",
    coverage: "Filed beside the repository and question-and-answer collections, so a disclosure and the code and discussion around it can be read together.",
    capabilities: [
      {
        title: "Search by what is affected",
        body: "Describe the component or the behaviour in plain words and reach the disclosures that concern it.",
        detail: ["Held as records", "Filed beside the code", "Traceable to where it was published"],
      },
      { title: "Read it beside the code", body: "Repositories and developer discussion sit on neighbouring shelves, so a disclosure and its context arrive together." },
      { title: "Follow it back", body: "Every record keeps the address it was read from, so a disclosure can be checked where it was made." },
    ],
    limits: [
      "It is not a scanner and it is not an alerting service. Finding a disclosure is not being told that you are exposed to it.",
      "A disclosure is what was published. Whether it applies to your version, your configuration or your deployment is a separate judgement.",
    ],
    relatedApp: { slug: "code", name: "Code", note: "The Code workspace reads this collection when a question is about a dependency rather than about your own code." },
  },
  {
    key: "entities",
    source: "storage",
    slug: "named-entities",
    serpTitle: "Company and organisation search — askFinz",
    eyebrow: "Reference collection",
    title: "Named entities, on a shelf of their own",
    seoTitle: "Named organisations and entities",
    lede: "Organisations, places and other named things filed as records in their own right, rather than only as mentions inside something else.",
    unit: "entities",
    record: "One named thing, kept as its own record rather than as a mention buried in another document.",
    coverage: "Filed as records so a name is a thing that can be looked up, not only a string that appears in documents about other subjects.",
    capabilities: [
      {
        title: "Start from the name",
        body: "Ask about an organisation or a place and reach the record for it, rather than the first document that happens to mention it.",
        detail: ["Held as records", "Looked up by name", "Traceable to where it was published"],
      },
      { title: "Read it beside the material", body: "Entities sit next to the collections that refer to them, so a name and the work around it can be read together." },
      { title: "Follow it back", body: "Every record keeps the address it was read from, so what is held about a name can be checked." },
    ],
    limits: [
      "It is not a company register and it is not a source of truth about who anyone is. It records what was published, not what is official.",
      "It is not a background check, and it must not be used as one.",
    ],
    relatedApp: { slug: "finance", name: "Finance", note: "Finance reads this collection when a question starts from a name rather than from a filing." },
  },
  {
    key: "ai_models",
    source: "storage",
    slug: "ai-models",
    serpTitle: "AI model search: machine-learning model records — askFinz",
    eyebrow: "Reference collection",
    title: "Published machine-learning models",
    seoTitle: "Machine-learning model records",
    lede: "Models published for others to use, held as records — so one can be found by what it is for rather than by what it is called.",
    unit: "models",
    record: "One published model, filed as a record rather than as a page describing it.",
    coverage: "Filed beside the repository and research collections, so a model and the work it came out of sit within reach of each other.",
    capabilities: [
      {
        title: "Find one by what it does",
        body: "Describe the task in plain words. Model names carry almost no information about their purpose, which is exactly the problem.",
        detail: ["Held as records", "Filed beside the code", "Traceable to where it was published"],
      },
      { title: "Read it beside the research", body: "Repositories and papers are on neighbouring shelves, so a model and the work behind it turn up in one search." },
      { title: "Follow it back", body: "Every record keeps the address it was read from, so a model can be checked where it was published." },
    ],
    limits: [
      "It is not a benchmark table. Nothing here ranks models or tells you which one is better at your task.",
      "It is not a download or a hosting service. The record says a model was published; running it is somewhere else entirely.",
    ],
    relatedApp: { slug: "code", name: "Code", note: "The Code workspace reads this collection when the question is which model exists for a job, not how to write one." },
  },
  {
    /* NINETY-ODD RECORDS, AND LISTED ANYWAY. It is a real shelf that is really filling; the honest
       framing is to say so rather than to hide it until the number flatters us or to write copy
       that implies more than ninety records. The `limits` below say it in the reader's words. */
    key: "insider_trades",
    source: "storage",
    slug: "insider-transactions",
    serpTitle: "Insider trading transaction search — askFinz",
    eyebrow: "Reference collection",
    title: "Insider transactions, as disclosed",
    seoTitle: "Insider transaction records",
    lede: "Share dealings disclosed by the people closest to a company, held as records. This shelf has only just started filling — it is listed because the records are real, not because the coverage is.",
    unit: "transactions",
    record: "One disclosed dealing, filed as a record rather than as a story written about it.",
    coverage: "Filed beside the filings and company collections, so a disclosure and the company behind it are on neighbouring shelves.",
    capabilities: [
      {
        title: "Reach the disclosure itself",
        body: "The record as it was disclosed, rather than the commentary written around it.",
        detail: ["Held as records", "Filed beside the filings", "Traceable to where it was published"],
      },
      { title: "Read it beside the filings", body: "Regulatory filings and company records sit on neighbouring shelves, so a dealing and its context can be read together." },
    ],
    limits: [
      "It is small, and it is new. Treat it as a shelf that is starting rather than one you can draw a conclusion from.",
      "It is not investment advice, and a disclosed dealing is not a signal. It is a record that something was disclosed.",
    ],
    relatedApp: { slug: "finance", name: "Finance", note: "Finance reads this collection alongside regulatory filings, where the company context already is." },
  },
  {
    /* ── LISTED THOUGH NO FEED CAN SEE IT YET. DO NOT DELETE THIS ENTRY. ──────────────────────
       This collection is real and holds tens of thousands of records. It appears in NEITHER the
       corpora insights payload NOR the storage feed, because neither enumerates it — a BACKEND
       ENUMERATION GAP, not an empty shelf. Anyone who later finds it "missing from the feed" and
       removes the entry will be deleting a page for a collection that exists.

       WHAT THAT MEANS ON THE PAGE. `SimpleCollectionPage` looks its row up in the storage feed,
       finds nothing, and returns `points: null` — which removes the whole live-figure aside. The
       page renders as prose, with no dash and no zero, exactly as it does when the index is
       unreachable. The figure appears on its own the moment the feed learns to enumerate it, and
       nothing here needs changing when that happens.

       IT IS NOT THE `research` CORPUS ABOVE. That one (/collections/research-and-data) describes
       papers, reports and the data behind them — three shelves the feeds do enumerate. This is
       general research material held separately from all three, and it gets its own slug so the
       two can never be read as the same page.

       THE DUPLICATE `key` IS DELIBERATE AND IS SAFE. `key` names a row in the feed given by
       `source`, and the two feeds are separate namespaces: the corpus entry's "research" is an
       insights key, this one's is a storage collection name. Every lookup that could confuse them
       resolves by SLUG (`getCorpus`, the route, the sitemap) or splits on `source` first
       (`CORPUS_KEYS`, `STORAGE_COLLECTION_KEYS`). The two that resolve by key take the FIRST match
       and so still reach the corpus — `getCorpusDepth`, and the map in
       components/marketing/solution-sections.tsx, which is built first-wins for this reason. ── */
    key: "research",
    source: "storage",
    slug: "general-research",
    serpTitle: "Research record search — askFinz",
    eyebrow: "Reference collection",
    title: "General research material, kept on its own shelf",
    seoTitle: "General research records",
    lede: "Research material held as records in its own right, apart from the papers, reports and data sets filed under the research corpus — so work that belongs to none of those three is still reachable.",
    unit: "records",
    record: "One piece of research material, filed as a record rather than as a page describing it.",
    coverage: "Filed next to the research corpus rather than inside it, so a question can reach either without having to choose which of the two it wanted first.",
    capabilities: [
      {
        title: "Reach material the other shelves do not hold",
        body: "Work that is neither a paper, a report nor a data set has somewhere to be, instead of being filed under whichever of the three fits worst.",
        detail: ["Held as records", "Filed beside the research", "Traceable to where it was published"],
      },
      { title: "Read it beside the papers", body: "It sits next to the research corpus, so this material and the published work around it turn up in the same search." },
      { title: "Follow it back", body: "Every record keeps the address it was read from, so anything drawn from it can be checked at the source." },
    ],
    limits: [
      "It is not the papers collection. Peer-reviewed work, preprints and the data behind them are on /collections/research-and-data; this is what sits outside those three shelves.",
      "It is not ranked and it is not a reading list. Nothing here tells you which piece of work carries more weight than another.",
    ],
    relatedApp: { slug: "research", name: "Research", note: "Research reads this collection when the material a question needs is not a paper, a report or a data set." },
  },
];

/**
 * Every key that must resolve in the corpora insights payload.
 *
 * The storage-backed entries are excluded on purpose — they are not in that payload and never will
 * be. They are guarded too, against the storage feed instead; see scripts/check-corpora.mjs.
 */
export const CORPUS_KEYS = CORPORA.filter((c) => c.source !== "storage").map((c) => c.key);

/** The collections whose figures come from the storage feed rather than the insights payload. */
export const STORAGE_COLLECTION_KEYS = CORPORA.filter((c) => c.source === "storage").map((c) => c.key);

export function getCorpus(slug: string): CorpusEntry | undefined {
  return CORPORA.find((c) => c.slug === slug);
}

/**
 * The depth fields for one corpus, looked up by its INSIGHTS KEY rather than its URL slug.
 *
 * WHY A LOOKUP AND NOT A PROP. app/collections/[key]/page.tsx passes a fixed prop set to
 * CorpusPageSSR — key, slug, eyebrow, title, seoTitle, lede, unit, capabilities, coverage,
 * relatedApp, variables, kindTitle — and is owned elsewhere. Adding `questions` there was not an
 * option, so CorpusPage resolves them itself from the corpus key it is already given. Any of these
 * passed as an explicit prop wins, which is how the six hand-written /apps pages used to carry
 * their own before those routes became redirects.
 *
 * `law` used to be absent here because two /apps pages shared that key and needed different depth
 * copy. They are one page now, so it has one entry and one set, resolved like every other.
 */
export function getCorpusDepth(key: string):
  | Pick<CorpusEntry, "questions" | "askIntro" | "returns" | "limits" | "workflow">
  | undefined {
  const c = CORPORA.find((x) => x.key === key);
  if (!c) return undefined;
  return {
    questions: c.questions,
    askIntro: c.askIntro,
    returns: c.returns,
    limits: c.limits,
    workflow: c.workflow,
  };
}
